import { useEffect, useState } from 'react';
import { Button } from '../button/Button';
import {
  fetchWeaponTemplates,
  importTemplates,
  type WeaponTemplateResponse,
} from '../../api/games';
import type { Language } from '../../api/useLanguage';
import styles from './WeaponTemplatePickerModal.module.css';

type Props = {
  code: string;
  moderatorName: string;
  lang: Language;
  onClose: () => void;
  onImported: () => void;
};

const DIFFICULTY_LABELS = { easy: 'Easy', moderate: 'Moderate', hard: 'Hard' };
const LOCATION_LABELS = {
  anywhere: 'Anywhere',
  cabin: 'Cabin',
  university: 'University',
  travel: 'Travel',
};

export function WeaponTemplatePickerModal({
  code,
  moderatorName,
  lang,
  onClose,
  onImported,
}: Props) {
  const [templates, setTemplates] = useState<WeaponTemplateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterLanguage, setFilterLanguage] = useState<Language>(lang);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWeaponTemplates()
      .then(setTemplates)
      .catch(() => setError('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter((t) => {
    if (filterDifficulty && t.difficulty !== filterDifficulty) return false;
    if (filterLocation && t.location !== filterLocation) return false;
    if (filterLanguage && t.language !== filterLanguage) return false;
    return true;
  });

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    setError(null);
    try {
      await importTemplates(code, moderatorName, [...selected]);
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
      setImporting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Weapon templates</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.filters}>
          <select
            aria-label="Filter by language"
            className={styles.select}
            value={filterLanguage}
            onChange={(e) => setFilterLanguage(e.target.value as Language)}
          >
            <option value="en">English</option>
            <option value="no">Norsk</option>
          </select>
          <select
            aria-label="Filter by difficulty"
            className={styles.select}
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
          >
            <option value="">All difficulties</option>
            {Object.entries(DIFFICULTY_LABELS).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by location"
            className={styles.select}
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
          >
            <option value="">All locations</option>
            {Object.entries(LOCATION_LABELS).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
          <div className={styles.selectAllRow}>
            <button
              type="button"
              className={styles.selectAllBtn}
              onClick={() => setSelected(new Set(filtered.map((t) => t.id)))}
              disabled={filtered.length === 0}
            >
              Select all
            </button>
            {selected.size > 0 && (
              <>
                <button
                  type="button"
                  className={styles.selectAllBtn}
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </button>
                <span className={styles.selectedCount}>{selected.size} selected</span>
              </>
            )}
          </div>
        </div>

        <div className={styles.grid}>
          {loading && <p className={styles.status}>Loading…</p>}
          {!loading && filtered.length === 0 && (
            <p className={styles.status}>No templates match the filters.</p>
          )}
          {filtered.map((t) => (
            <label
              key={t.id}
              className={`${styles.templateCard} ${selected.has(t.id) ? styles.templateCardSelected : ''}`}
            >
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={selected.has(t.id)}
                onChange={() => toggle(t.id)}
              />
              <div className={styles.templateInfo}>
                <span className={styles.templateName}>{t.name}</span>
                <div className={styles.templateMeta}>
                  <span className={`${styles.metaBadge} ${styles[`diff_${t.difficulty}`]}`}>
                    {DIFFICULTY_LABELS[t.difficulty]}
                  </span>
                  <span className={styles.metaBadge}>{LOCATION_LABELS[t.location]}</span>
                </div>
                <p className={styles.templateDesc}>{t.description}</p>
              </div>
            </label>
          ))}
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onImport} disabled={selected.size === 0 || importing}>
            {importing
              ? 'Importing…'
              : `Import ${selected.size > 0 ? selected.size : ''} weapon${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
